import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Pressable, type StyleProp, StyleSheet, View, type ViewStyle } from 'react-native';
import { useStyle } from 'react-native-style-utilities';

import { AnimatedViews, DecorativeText, FallbackImage, IllusEmpty_2, theme } from '@sg/garnish';

export const GiftCardHeader = (props: GiftCardHeaderProps) => {
  const {
    isFetchingContent,
    headerTitle,
    headerBackgroundColor,
    headerTextColor,
    headerImage,
    headerImageA11y,
    style,
    onPress,
  } = props;

  const { formatMessage } = useIntl();

  const textStyle = useStyle(() => ({ color: headerTextColor }), [headerTextColor] ); // prettier-ignore
  const backgroundStyle = useStyle(() => ({ backgroundColor: headerBackgroundColor }), [headerBackgroundColor]); // prettier-ignore
  const loadingContainerStyles = [styles.container, styles.loadingContainer, style];
  const containerStyles = [styles.container, backgroundStyle, style];

  // ─── Rendering ───────────────────────────────────────────────────────────────

  if (isFetchingContent) {
    return (
      <AnimatedViews.Pulse style={loadingContainerStyles}>
        <View style={styles.titlePlaceholder} />

        <View style={styles.imagePlaceholder} />
      </AnimatedViews.Pulse>
    );
  }

  return (
    <AnimatedViews.Reveal>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={formatMessage(messages.a11yLabel)}
        style={containerStyles}
        onPress={onPress}
      >
        <DecorativeText sizeMatch={['24']} style={[textStyle, styles.text]}>
          {headerTitle}
        </DecorativeText>

        <FallbackImage
          style={styles.image}
          baseUrl={headerImage}
          aria-label={headerImageA11y}
          defaultImage={IllusEmpty_2}
          contentFit="contain"
          contentfulOptions={contentfulOptions}
        />
      </Pressable>
    </AnimatedViews.Reveal>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const contentfulOptions = {
  fm: 'webp' as const,
  q: 90,
  w: 80,
  h: 60,
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  a11yLabel: {
    defaultMessage: 'Open gift cards purchase screen',
    description: 'Gift Cards Screen | Header | A11y label',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing['6'],
    padding: theme.spacing['6'],
    borderRadius: theme.radius.large,
  },
  text: {
    flex: 1,
  },
  image: {
    width: 80,
    height: 60,
  },
  loadingContainer: {
    backgroundColor: theme.colors.OPACITY.DARK_KALE.ALMOST_TRANSPARENT,
  },
  titlePlaceholder: {
    width: '55%',
    height: 18,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  imagePlaceholder: {
    width: 80,
    height: 60,
    borderRadius: theme.radius.medium,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type GiftCardHeaderProps = {
  isFetchingContent: boolean;
  headerTitle: string;
  headerBackgroundColor: string;
  headerTextColor: string;
  headerImage?: string;
  headerImageA11y?: string;
  style?: StyleProp<ViewStyle>;
  onPress: () => void;
};
